import React, { useState, useEffect } from 'react';
import { Table, Button, Popconfirm, Space, Card, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { useCity } from '../../store/cityContext';
import { City } from '../../types/city';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useAuth } from '../../store/AuthContext';
import PageNavigation from '../Layout/PageNavigation';

const { Text } = Typography;

interface Props {
    setSelectedCity: (city: City | null) => void;
}

const CityList: React.FC<Props> = ({ setSelectedCity }) => {
    const { t } = useTranslation();
    const { cities, deleteCity } = useCity();
    const { hasPermission } = useAuth();

    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handleDelete = async (id?: string) => {
        if (!id) return;
        await deleteCity(id);
    };

    const columns = [
        { title: t('form.cityNameKurdish'), dataIndex: 'name_ku' },
        { title: t('form.cityNameEnglish'), dataIndex: 'name_eng' },
        { title: t('form.cityNameArabic'), dataIndex: 'name_ar' },
        {
            title: t('actions'),
            render: (_: any, record: City) => (
                <Space>
                    {hasPermission("city.update") && (
                        <Button
                            type="primary"
                            icon={<EditOutlined />}
                            size={isMobile ? 'small' : 'middle'}
                            onClick={() => setSelectedCity(record)}
                        >
                            {!isMobile && t('edit')}
                        </Button>
                    )}
                    {hasPermission("city.delete") && (
                        <Popconfirm
                            title={t('sure_to_delete')}
                            onConfirm={() => handleDelete(record.id)}
                        >
                            <Button
                                type="primary"
                                danger
                                icon={<DeleteOutlined />}
                                size={isMobile ? 'small' : 'middle'}
                            >
                                {!isMobile && t('delete')}
                            </Button>
                        </Popconfirm>
                    )}
                </Space>
            ),
        },
    ];

    // Responsive: Card layout for mobile
    if (isMobile) {
        return (
            <div style={{ padding: 8 }}>
                <PageNavigation title={t('cities') as string} />
                {cities.map(city => (
                    <Card
                        key={city.id}
                        size="small"
                        style={{ marginBottom: 12 }}
                        bodyStyle={{ padding: 12 }}
                    >
                        <Text strong>{t('form.cityNameKurdish')}:</Text> {city.name_ku}<br />
                        <Text strong>{t('form.cityNameEnglish')}:</Text> {city.name_eng}<br />
                        <Text strong>{t('form.cityNameArabic')}:</Text> {city.name_ar}
                        <div style={{ marginTop: 10 }}>
                            {hasPermission("city.update") && (
                                <Button
                                    type="primary"
                                    icon={<EditOutlined />}
                                    size="small"
                                    onClick={() => setSelectedCity(city)}
                                    style={{ marginRight: 6 }}
                                />
                            )}
                            {hasPermission("city.delete") && (
                                <Popconfirm
                                    title={t('sure_to_delete')}
                                    onConfirm={() => handleDelete(city.id)}
                                >
                                    <Button
                                        type="primary"
                                        danger
                                        icon={<DeleteOutlined />}
                                        size="small"
                                    />
                                </Popconfirm>
                            )}
                        </div>
                    </Card>
                ))}
            </div>
        );
    }

    return (
        <div style={{ width: '100%', overflowX: 'auto', padding: 8 }}>
            <PageNavigation title={t('cities') as string} />
            <Table
                rowKey="id"
                dataSource={cities}
                columns={columns}
                pagination={{ pageSize: 6, size: 'small' }}
                scroll={{ x: 'max-content' }}
                size="middle"
            />
        </div>
    );
};

export default CityList;
